import PropTypes from "prop-types";
import React, { Component } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import moment from "moment-timezone";

import { MSelect, MSpinner } from "components/Shared";
import TextBlock from "components/Shared/TextBlock";
import SummaryView from "components/QualityAssurance/Views/SummaryView";
import SummaryViewQA2 from "components/QualityAssurance/Views/SummaryViewQA2";
import { QUALITY_REVIEW_ASSIGN, CLOCK_IN_OR_OUT_FUNCTION } from "typeConstants/rights";
import {
  BUSOP_AGENT,
  QUALITY_COMPLIANCE_AUDITOR,
  BUSOP_ASSISTANT_VP,
  BUSOP_DIRECTOR,
  BUSOP_MANAGER,
  VALUATION_QUALITY_ASSOCIATE,
  VALUATION_QUALITY_MANAGER,
} from "typeConstants/employeeRoles";
import { getEmployeesByRoles } from "requests/employee";

import StarRatings from "react-star-ratings";
import logo from "static/images/logo.png";
import { addSaveEvent } from "requests/caseEvent";
import { postQA2Feedback } from "requests/caseFeedback";
import { categoryCodes } from "typeConstants/caseFeedbackCategoryCodes";
import { validateGeoTagForm } from "requests/qualityAssurance/index.ts";
import { QueueTypeEnum, CaseTimeLogWorkTypeEnum } from "interfaces/Mueller.Business.Models.ts";
// renamed so no one accidentally calls stop timer when they should be calling the function on the class to stop the timer
import { startTimer as startTimerApi, stopTimer as stopTimerApi, pingTimer } from "requests/caseUserTimer/index.ts";
import { PhoenixFormsContext } from "components/PhoenixFormsV4/index.tsx";

class CaseView extends Component {
  static contextType = PhoenixFormsContext;

  constructor(props) {
    super(props);
    this.state = {
      headerCollapsed: false,
      showRejectDialog: false,
      showSendToManagerDialog: false,
      showCompleteCaseDialog: false,
      showErrorsDialog: false,
      rejectReason: "",
      sendCaseNote: "",
      rejectSendManager: true,
      assignToUserId: 0,
      summaryKey: 0,
      errorsForDialog: [],
      blockingTimeLog: true,
      qaQueueUsers: [],
      ratingScore: 0,
      ratingComment: "",
      displayingRatingSurvey: false,
      categoryCode: "",
      pendingAudits: null,
    };
    this.qaView = React.createRef();
    this.rejectReasonField = React.createRef();
    this.sendToManagerReasonField = React.createRef();
    this.saveCase = this.saveCase.bind(this);
    this.onReadyForQA = this.onReadyForQA.bind(this);
    this.completeCase = this.completeCase.bind(this);
    this.returnCase = this.returnCase.bind(this);
    this.sendCase = this.sendCase.bind(this);
    this.frameRefreshFunc = this.frameRefreshFunc.bind(this);
    this.addCaseTimeLog = this.addCaseTimeLog.bind(this);
    this.shouldDisablePhotoTags = this.shouldDisablePhotoTags.bind(this);
    this.mounted = { current: true };
    this.submitRating = this.submitRating.bind(this);
    this.displayRatingSurvey = this.displayRatingSurvey.bind(this);
    this.startSubmitAppraiserRating = this.startSubmitAppraiserRating.bind(this);
    this.stopTimer = this.stopTimer.bind(this);
    this.submittingTimeLog = false;
  }

  componentDidMount() {
    window.addEventListener("unload", this.addCaseTimeLog);
    const { setQASaveDataFunction, getActiveClock, setQAPaused } = this.props;

    setQASaveDataFunction(() =>
      this.saveCase(false, () => {
        setQAPaused(false);
        addSaveEvent(this.props.qaCase.controlIdNumber);
      }),
    );
    if (this.props.profile.rights.includes(CLOCK_IN_OR_OUT_FUNCTION)) getActiveClock(new Date().getTimezoneOffset());
    if (this.props.qaCase.controlIdNumber) this.props.getAllCaseFormsRedux(this.props.qaCase.controlIdNumber);
    // Ensure we have fresh QL data available
    this.props.getQuickLists(true);
  }

  componentDidUpdate(prevProps) {
    const {
      qaCase,
      summaryViewEnabled,
      getCaseSketch,
      getCaseAlerts,
      getCaseInfo,
      pnxValidation,
      willLogOut,
      timeSpent,
      completingCase,
      escalatingCase,
      rejectingCase,
      getAllCaseFormsRedux,
      setQAPaused,
      qaTimerId,
      qaTimerPingIntervalSeconds,
    } = this.props;

    const { isSaving } = this.context;

    // Need to submit time log before the logout otherwise the call will fail
    if (willLogOut && !this.state.blockingTimeLog && timeSpent) {
      this.addCaseTimeLog();
    }

    // timer just started
    if (!prevProps.qaTimerId && qaTimerId) {
      if (this.timerPingInterval) window.clearInterval(this.timerPingInterval);
      this.timerPingInterval = window.setInterval(
        () => pingTimer(this.props.qaTimerId, this.mounted),
        qaTimerPingIntervalSeconds * 1000,
      );
    }

    if (qaCase.controlIdNumber && qaCase.controlIdNumber !== prevProps.qaCase.controlIdNumber) {
      getCaseInfo(qaCase.controlIdNumber);
      getAllCaseFormsRedux(qaCase.controlIdNumber);
      if (!completingCase && !escalatingCase && !rejectingCase) {
        getCaseAlerts();
        if (summaryViewEnabled) {
          getCaseSketch({ ControlNumber: qaCase.controlIdNumber });
        }

        validateGeoTagForm(qaCase.controlIdNumber);

        if (this.props.rights.includes(QUALITY_REVIEW_ASSIGN)) {
          let rolesToGet =
            qaCase.qaQueueTypeId === QueueTypeEnum.AppraisalQa
              ? [VALUATION_QUALITY_ASSOCIATE, VALUATION_QUALITY_MANAGER]
              : [BUSOP_AGENT, BUSOP_ASSISTANT_VP, BUSOP_DIRECTOR, BUSOP_MANAGER, QUALITY_COMPLIANCE_AUDITOR];
          getEmployeesByRoles(
            rolesToGet,
            (response) => {
              this.setState({
                qaQueueUsers: response.map((u) => ({ label: u.firstLastName, value: u.internetUserId })),
              });
            },
            this.mounted,
          );
        }
        if (!this.props.qaReady) {
          this.onReadyForQA();
        }
      }
    } else if (qaCase.controlIdNumber && qaCase.controlIdNumber === prevProps.qaCase.controlIdNumber) {
      if (this.prevIsSaving && !isSaving) {
        // phoenix forms save finished
        setQAPaused(true);
        getCaseInfo(qaCase.controlIdNumber, () => setQAPaused(false));
      }

      if (pnxValidation.fetchingResult === false && prevProps.pnxValidation.fetchingResult === true) {
        this.onAfterValidation();
      }
    }

    if (this.props.quickLists && this.props.quickLists.length) {
      let auditResults = this.props.quickLists.find((s) => s.templateCode === "QualityAssuranceAuditReview");
      if (auditResults) {
        let today = new Date();
        let dateOfResults = new Date(auditResults?.count?.asOfDate);
        today.setHours(0, 0, 0, 0);
        dateOfResults.setHours(0, 0, 0, 0);
        if (today > dateOfResults) {
          this.props.getQuickListCount(auditResults);
          this.props.setQAPaused(true);
        } else if (auditResults.count) {
          this.props.setQAPaused(false);
          let hasPendingAudits = auditResults.count.recordCount > 0;
          if (hasPendingAudits !== this.state.pendingAudits) {
            this.setState({ pendingAudits: hasPendingAudits });
          }
        }
      }
    }

    // componentDidUpdate does not track context changes, saving as a "ref"
    this.prevIsSaving = isSaving;
  }

  componentWillUnmount() {
    // If any case action just took place and we do not yet have a
    // new case, block time logging as the action already logged time.
    if (!this.state.blockingTimeLog && this.props.timeSpent) {
      this.addCaseTimeLog();
    }
    window.removeEventListener("unload", this.addCaseTimeLog);
    this.mounted.current = false;
  }

  // some stupid lint rule is telling me this belongs here even tho it definitely belongs at the top of the class
  timerPingInterval = null;

  onAfterValidation() {
    const { pnxValidation } = this.props;
    if (pnxValidation.validateSuccess === false) {
      let groupedResults = {};
      pnxValidation?.validationResults.map((result) => {
        if (groupedResults[result.formName]) {
          groupedResults[result.formName].push({ ...result });
        } else {
          groupedResults[result.formName] = [];
          groupedResults[result.formName].push({ ...result });
        }
      });
      this.setState({
        showErrorsDialog: true,
        errorsForDialog: groupedResults,
      });
    }
    pnxValidation.callback(pnxValidation.validateSuccess);
  }

  addCaseTimeLog() {
    if (this.props.qaReady && !this.submittingTimeLog) {
      this.submittingTimeLog = true;
      const {
        addReviewCaseTimeLog,
        qaCase: { controlIdNumber, qaQueueTypeId },
        profile: { internetUserId },
        timeSpent,
        setQATimeSpent,
      } = this.props;
      let timeLog = {
        controlIdNumber: controlIdNumber,
        internetUserId: internetUserId,
        workDate: moment().format("YYYY-MM-DD"),
        workTypeId: getCaseTimeLogWorkType(qaQueueTypeId),
        timeMinutes: Number(timeSpent / 60),
      };
      addReviewCaseTimeLog(timeLog);
      setQATimeSpent(0.0, () => {
        this.submittingTimeLog = false;
      });

      this.stopTimer();
    }
  }

  stopTimer(callback) {
    if (this.props.qaTimerId !== null) {
      stopTimerApi(this.props.qaTimerId, this.mounted, callback);
      this.props.setQATimerInfo(null, null);
      if (this.timerPingInterval) window.clearInterval(this.timerPingInterval);
    } else {
      callback?.();
    }
  }

  onReadyForQA() {
    const { setQAReady, setQAPaused } = this.props;
    setQAReady(true);
    setQAPaused(false);
    this.setState({ blockingTimeLog: false });
  }

  //if controlNumber is provided keepGettingCases is counted as false
  //if its not provided this routine will just get the next case as it always did
  getNextCase(controlNumber = null) {
    const {
      setQAReady,
      setQAPaused,
      setQAValidationErrors,
      getNextCase,
      keepGettingCases,
      history,
      resetQACase,
      setQATimerInfo,
    } = this.props;

    setQAReady(false);
    if (!keepGettingCases && !controlNumber) {
      this.stopTimer();
      history.push("/");
      return;
    }
    setQAPaused(true);
    setQAValidationErrors([]);
    resetQACase(() => {
      this.stopTimer(() =>
        getNextCase({ ControlNumber: controlNumber }, (resp) =>
          this.startTimer(resp.controlIdNumber, (response) => setQATimerInfo(response.id, response.pingRateSeconds)),
        ),
      );
    });
  }

  // callback is called with type StartTimerResult passed as the param
  startTimer = (controlNumber, callback) => {
    startTimerApi(controlNumber, this.mounted, callback);
  };

  frameRefreshFunc() {
    const { summaryViewEnabled } = this.props;
    return this.qaView.current && summaryViewEnabled ? this.qaView.current.refreshFrame : null;
  }

  saveCase(needsValidate, followUpAction) {
    const { setQAPaused, summaryViewEnabled } = this.props;
    setQAPaused(true);
    if (summaryViewEnabled) this.saveNewView(needsValidate, followUpAction);
    else this.saveLegacyView(needsValidate, followUpAction);
  }

  saveLegacyView(needsValidate, followUpAction) {
    const {
      setQAPaused,
      generatePDF,
      getCaseAlerts,
      makeToast,
      qaCase: { controlIdNumber },
    } = this.props;
    this.qaView.current.saveData(() => {
      if (needsValidate) {
        this.qaView.current.validate((validationSuccess) => {
          if (followUpAction) {
            followUpAction(validationSuccess);
          } else {
            generatePDF(controlIdNumber);
            getCaseAlerts();
            this.setState({ summaryKey: this.state.summaryKey + 1 });
          }
          setQAPaused(false);
          makeToast("Case Saved Successfully", "success");
        });
      } else {
        if (followUpAction) {
          followUpAction();
        } else {
          generatePDF(controlIdNumber);
          getCaseAlerts();
          this.qaView.current.refreshFrame();
          this.setState({ summaryKey: this.state.summaryKey + 1 });
        }
        setQAPaused(false);
        makeToast("Case Saved Successfully", "success");
      }
    });
  }

  saveNewView(needsValidate, followUpAction) {
    const {
      setQAPaused,
      validateForms,
      qaCase: { controlIdNumber },
      generatePDF,
      getCaseInfo,
      getCaseAlerts,
      makeToast,
    } = this.props;

    this.saveAllForms();

    if (followUpAction) {
      if (needsValidate) {
        validateForms(controlIdNumber, null, followUpAction);
      } else {
        followUpAction();
      }
    } else {
      generatePDF(controlIdNumber);
      getCaseAlerts();
      getCaseInfo(controlIdNumber, () => {
        setQAPaused(false);
        makeToast("Case Saved Successfully", "success");
      });
    }
  }

  saveAllForms() {
    const { save } = this.context;

    save();
  }

  completeCase() {
    const {
      setQAShowCompletedCaseDialog,
      setQACompletingCase,
      qaCase: { controlIdNumber, qaQueueId, qaQueueTypeId },
      profile: { internetUserId },
      timeSpent,
      completeCase,
      setQAPaused,
      makeToast,
      setQATimeSpent,
      completingCase,
    } = this.props;

    if (completingCase) return;

    let timeLog = {
      controlIdNumber: controlIdNumber,
      internetUserId: internetUserId,
      workDate: moment().format("YYYY-MM-DD"),
      workTypeId: getCaseTimeLogWorkType(qaQueueTypeId),
      timeMinutes: Number(timeSpent / 60),
    };

    setQAShowCompletedCaseDialog(false);
    setQACompletingCase(true);

    let completeRoutine = (validationSuccess) => {
      if (!validationSuccess) {
        setQACompletingCase(false);
        setQAPaused(false);
        window.scrollTo(0, 0);
        makeToast("Case Saved with Errors", "error");
      } else {
        const data = {
          ControlIdNumber: controlIdNumber,
          QaQueueId: qaQueueId,
          TimeLog: timeLog,
          InternetUserId: internetUserId,
          LocalOffsetMinutes: new Date().getTimezoneOffset(),
        };
        completeCase(data, () => {
          // DONT WORRY about calling this.stopTimer here because displayRatingSurvey will eventually call it
          if (this.props.requestSuccessful) {
            setQATimeSpent(0.0);
            this.setState({ blockingTimeLog: true, categoryCode: categoryCodes.QA2_COMPLETED_ABOUT_APPRAISER });
            this.displayRatingSurvey();
          } else {
            setQAPaused(false);
          }
          setQACompletingCase(false);
        });
      }
    };
    this.saveCase(true, completeRoutine);
  }

  returnCase() {
    const {
      setQAPaused,
      setQAShowRejectDialog,
      setQARejectingCase,
      setQATimeSpent,
      timeSpent,
      returnCase,
      qaCase: { controlIdNumber, qaQueueId, qaQueueTypeId },
      profile: { internetUserId },
      rejectingCase,
    } = this.props;
    const { rejectReason, rejectSendManager } = this.state;

    if (rejectingCase) return;

    let timeLog = {
      controlIdNumber: controlIdNumber,
      internetUserId: internetUserId,
      workDate: moment().format("YYYY-MM-DD"),
      workTypeId: getCaseTimeLogWorkType(qaQueueTypeId),
      timeMinutes: Number(timeSpent / 60),
    };
    setQAPaused(true);
    setQAShowRejectDialog(false);
    setQARejectingCase(true);

    let returnRoutine = () => {
      const data = {
        ControlIdNumber: controlIdNumber,
        QaQueueId: qaQueueId,
        QaQueueTypeId: qaQueueTypeId,
        RejectionNotes: rejectReason,
        SendCopyToManager: rejectSendManager,
        TimeLog: timeLog,
        internetUserId: internetUserId,
        localOffsetMinutes: new Date().getTimezoneOffset(),
      };
      returnCase(data, (res) => {
        if (this.props.requestSuccessful) {
          setQATimeSpent(0.0);
          this.setState({ blockingTimeLog: true, categoryCode: categoryCodes.QA2_RETURNED_ABOUT_APPRAISER });
          // DONT WORRY about calling this.stopTimer here because displayRatingSurvey will eventually call it
          this.displayRatingSurvey();
        } else if (res?.isInvalid) {
          const validationMessage = res.validationMessage ?? "Return to inspector was invalid.";
          this.props.makeToast(validationMessage, "error");
          setQAPaused(false);
        } else {
          setQAPaused(false);
        }
        setQARejectingCase(false);
      });
    };
    this.saveCase(false, returnRoutine);
  }

  sendCase() {
    const {
      setQAPaused,
      setQAShowSendManagerDialog,
      setQAEscalatingCase,
      setQATimeSpent,
      timeSpent,
      sendToManager,
      qaCase: { controlIdNumber, qaQueueId, qaQueueTypeId },
      profile: { internetUserId },
      rights,
      sendToAgent,
      escalatingCase,
    } = this.props;

    const { sendCaseNote, assignToUserId } = this.state;

    if (escalatingCase) return;
    if (!assignToUserId) return;

    const canAssign = rights.includes(QUALITY_REVIEW_ASSIGN);

    let timeLog = {
      controlIdNumber: controlIdNumber,
      internetUserId: internetUserId,
      workDate: moment().format("YYYY-MM-DD"),
      workTypeId: getCaseTimeLogWorkType(qaQueueTypeId),
      timeMinutes: Number(timeSpent / 60),
    };
    setQAPaused(true);
    setQAShowSendManagerDialog(false);
    setQAEscalatingCase(true);

    const sendFunction = canAssign ? sendToAgent : sendToManager;

    let escalateRoutine = () => {
      const data = {
        ControlIdNumber: controlIdNumber,
        QaQueueId: qaQueueId,
        QaQueueTypeId: qaQueueTypeId,
        Notes: sendCaseNote,
        AssignToInternetUserId: assignToUserId,
        TimeLog: timeLog,
        RequestorInternetUserId: internetUserId,
      };
      sendFunction(data, () => {
        if (this.props.requestSuccessful) {
          setQATimeSpent(0.0);
          this.setState({ blockingTimeLog: true });
          this.stopTimer();
          this.getNextCase();
        } else {
          setQAPaused(false);
        }
        setQAEscalatingCase(false);
      });
    };
    this.saveCase(false, escalateRoutine);
  }

  shouldDisablePhotoTags() {
    const {
      showRejectDialog,
      showSendToManagerDialog,
      showCompleteCaseDialog,
      showSwitchCaseDialog,
      completingCase,
      rejectingCase,
      escalatingCase,
    } = this.props;
    return (
      showRejectDialog ||
      showSendToManagerDialog ||
      showCompleteCaseDialog ||
      showSwitchCaseDialog ||
      completingCase ||
      rejectingCase ||
      escalatingCase
    );
  }

  /** starts request to submit appraiser rating survey data */
  submitRating() {
    const { makeToast } = this.props;
    const { ratingScore, ratingComment } = this.state;
    if (ratingScore === 0) {
      return makeToast("Please select a star rating before submitting.");
    }
    if (ratingComment.length === 0) {
      return makeToast("Please leave a comment before submitting.");
    }
    if (ratingComment.length > 500) {
      return makeToast("You have exceeded the character limit of 500.");
    }
    this.startSubmitAppraiserRating();
    this.getNextCase();
  }

  startSubmitAppraiserRating() {
    const {
      qaCase: { controlIdNumber },
      makeToast,
    } = this.props;
    const { ratingScore, ratingComment, categoryCode } = this.state;
    if (controlIdNumber && categoryCode) {
      postQA2Feedback(controlIdNumber, categoryCode, ratingScore, ratingComment, (err) => err);
      makeToast("Thank you for your feedback.");
      this.setState({ ratingScore: 0, ratingComment: "", displayingRatingSurvey: false });
      return;
    } else {
      makeToast("Unable to collect your feedback.");
      return;
    }
  }

  displayRatingSurvey() {
    const {
      qaCase: { qaQueueTypeId },
    } = this.props;
    return qaQueueTypeId === QueueTypeEnum.AppraisalQa
      ? this.setState({ displayingRatingSurvey: true })
      : this.getNextCase();
  }

  render() {
    const {
      qaCase,
      qaReady,
      qaStarted,
      qaPaused,
      showRejectDialog,
      showSendToManagerDialog,
      showCompleteCaseDialog,
      showSwitchCaseDialog,
      switchCaseDialogControlNum,
      completingCase,
      rejectingCase,
      escalatingCase,
      setQAShowRejectDialog,
      setQAShowSendManagerDialog,
      setQAShowCompletedCaseDialog,
      setQASwitchCaseDialog,
      activeClock,
      rights,
    } = this.props;

    const { ratingScore, displayingRatingSurvey } = this.state;

    const { caseInfo } = qaCase;

    const rejectActions = [
      <Button
        className="m-t-10"
        key="continue"
        onClick={(e) => {
          this.returnCase();
          e.stopPropagation();
        }}
      >
        Continue
      </Button>,
      <Button className="m-t-10" key="cancel" onClick={() => setQAShowRejectDialog(false)}>
        Cancel
      </Button>,
    ];
    const sendCaseActions = [
      <Button
        className="m-t-10"
        key="continue"
        onClick={(e) => {
          this.sendCase();
          e.stopPropagation();
        }}
      >
        Continue
      </Button>,
      <Button className="m-t-10" key="cancel" onClick={() => setQAShowSendManagerDialog(false)}>
        Cancel
      </Button>,
    ];
    const completeCaseActions = [
      <Button
        className="m-t-10"
        key="continue"
        onClick={(e) => {
          this.completeCase();
          e.stopPropagation();
        }}
      >
        Continue
      </Button>,
      <Button className="m-t-10" key="cancel" onClick={() => setQAShowCompletedCaseDialog(false)}>
        Cancel
      </Button>,
    ];
    const switchCaseActions = qaPaused
      ? [
          <Button className="m-t-10" key="yes" disabled>
            Saving Case...
          </Button>,
        ]
      : [
          <Button className="m-t-10" key="yes" onClick={this.switchCase}>
            Yes
          </Button>,
          <Button className="m-t-10" key="nah" onClick={() => setQASwitchCaseDialog(false)}>
            No
          </Button>,
        ];
    const clockInActions = [
      <Button
        className="m-t-10"
        key="continue"
        onClick={() => {
          this.props.history.push("/qa-dashboard");
        }}
      >
        QA Dashboard
      </Button>,
    ];
    const pendingAuditsActions = [
      <Button className="m-t-10" key="continue" onClick={() => this.props.history.push("/")}>
        Home
      </Button>,
    ];
    const errorDialogOptions = [
      <Button
        className="m-t-10"
        key="errors-continue"
        onClick={() => this.setState({ showErrorsDialog: false, errorsForDialog: [] })}
      >
        Continue
      </Button>,
    ];

    const rejectToField =
      "You are about to send a field survey back to the field rep.\r\nPlease explain to management and the field rep why you are making an inquiry on this case:";
    const sendToManager =
      "You are about to send a field survey to your manager.\r\nPlease explain the reason behind escalating this case:";
    const rejectToAppraiser =
      "You are about to send an appraisal back to the appraiser.\r\nPlease explain to management and the appraiser why you are making an inquiry on this appraisal:";
    const sendToAgent = "You are about to send a field survey to an agent \r\nProvide an optional note to the agent.";

    const canAssign = rights.includes(QUALITY_REVIEW_ASSIGN);
    let needsClockIn =
      this.props.profile.rights.includes(CLOCK_IN_OR_OUT_FUNCTION) && !(activeClock && activeClock.clockInDate);
    let needsAuditReview = this.state.pendingAudits;

    return (
      <div className="qa-case">
        <Dialog id="dialog-clock-in" open={needsClockIn} sx={{ "& .MuiDialog-paper": { width: 500 } }}>
          <DialogTitle>Please Clock In</DialogTitle>
          <DialogContent>
            <TextBlock
              field={`
              You are not currently clocked in. Case production cannot begin until you are clocked in.
              Please either navigate to the QA Dashboard page to clock in or see your manager for a clock-in
              correction before beginning QA.`}
            />
          </DialogContent>
          <DialogActions>{clockInActions}</DialogActions>
        </Dialog>
        <Dialog
          id="dialog-clock-in"
          open={needsAuditReview && !needsClockIn}
          sx={{ "& .MuiDialog-paper": { width: 500 } }}
        >
          <DialogTitle>Please Review Your Audits</DialogTitle>
          <DialogContent>
            <TextBlock field={`Please review your audit results before you Start QA`} />
          </DialogContent>
          <DialogActions>{pendingAuditsActions}</DialogActions>
        </Dialog>
        <Dialog id="dialog-errors" open={this.state.showErrorsDialog} sx={{ "& .MuiDialog-paper": { width: 500 } }}>
          <DialogTitle>Forms Failed Validation</DialogTitle>
          <DialogContent>
            {Object.keys(this.state.errorsForDialog).map((formName) => {
              return (
                <>
                  <b>{formName}</b>
                  <ul key={formName}>
                    {this.state.errorsForDialog[formName]?.map((s) => {
                      return s.messages.map((p) => <li key={p}>{p}</li>);
                    })}
                  </ul>
                </>
              );
            })}
          </DialogContent>
          <DialogActions>{errorDialogOptions}</DialogActions>
        </Dialog>
        {/* Dialog Containers */}
        <div>
          <div>
            <Dialog open={showRejectDialog} sx={{ "& .MuiDialog-paper": { width: 500 } }}>
              <DialogTitle>{`Return To ${qaCase.qaQueueTypeId === 2 ? "Appraiser" : "Field"}`}</DialogTitle>
              <DialogContent sx={{ mt: 1 }}>
                <TextBlock field={qaCase.qaQueueTypeId === 2 ? rejectToAppraiser : rejectToField} />
                <TextField
                  inputRef={this.rejectReasonField}
                  variant="standard"
                  type={"text"}
                  fullWidth
                  // Appraisal QA allows 3000 chars, field allows 1000 chars
                  inputProps={{ maxLength: qaCase.qaQueueTypeId === 2 ? 3000 : 1000 }}
                  label={"Return Reason"}
                  onChange={(e) =>
                    this.setState((state) => {
                      if (state.rejectReason == e.target.value) {
                        return null;
                      }
                      return {
                        rejectReason: e.target.value,
                      };
                    })
                  }
                  required
                  multiline
                  rows={2}
                />
                <FormControlLabel
                  control={
                    <Switch
                      name="sendmanager"
                      defaultChecked
                      onChange={(e) =>
                        this.setState((state) => {
                          if (state.rejectSendManager == e.target.checked) {
                            return null;
                          }
                          return {
                            rejectSendManager: e.target.checked,
                          };
                        })
                      }
                    />
                  }
                  label="Send copy to manager"
                />
              </DialogContent>
              <DialogActions>{rejectActions}</DialogActions>
            </Dialog>
            <Dialog open={rejectingCase} sx={{ "& .MuiDialog-paper": { width: 400 } }}>
              <DialogTitle>Returning Case to Field...</DialogTitle>
              <DialogContent>
                <MSpinner />
              </DialogContent>
            </Dialog>
          </div>
          <div>
            <Dialog
              className="dialog-send-to-manager"
              open={showSendToManagerDialog}
              sx={{ "& .MuiDialog-paper": { width: 500 } }}
            >
              <DialogTitle>{canAssign ? "Return to Agent" : "Send to Manager"}</DialogTitle>
              <DialogContent sx={{ mt: 1 }}>
                <TextBlock field={canAssign ? sendToAgent : sendToManager} />
                <MSelect
                  placeholder={canAssign ? "Please select a user..." : "Please select a manager..."}
                  options={
                    canAssign
                      ? this.state.qaQueueUsers
                      : caseInfo && caseInfo.managers
                        ? caseInfo.managers.map((m) => ({
                            label: m.nameNumberDto.firstLastNameOnly,
                            value: m.internetUserId,
                          }))
                        : []
                  }
                  onChange={(option) => {
                    this.setState((state) => {
                      if (state.assignToUserId == option?.value) {
                        return null;
                      }
                      return {
                        assignToUserId: option?.value,
                      };
                    });
                  }}
                  name={canAssign ? "User" : "Manager:"}
                  className="md-full-width"
                  getMenuPortalTarget={() => document.querySelector(".dialog-send-to-manager")}
                />
                <TextField
                  inputRef={this.sendToManagerReasonField}
                  variant="standard"
                  type={"text"}
                  fullWidth
                  inputProps={{ maxLength: 1000 }}
                  label={canAssign ? "Note" : "Return Reason"}
                  onChange={(e) =>
                    this.setState((state) => {
                      if (state.sendCaseNote == e.target.value) {
                        return null;
                      }
                      return {
                        sendCaseNote: e.target.value,
                      };
                    })
                  }
                  required={!canAssign}
                  multiline
                  rows={3}
                />
              </DialogContent>
              <DialogActions>{sendCaseActions}</DialogActions>
            </Dialog>
            <Dialog open={escalatingCase} sx={{ "& .MuiDialog-paper": { width: 400 } }}>
              <DialogTitle>{canAssign ? "Sending Case to Agent..." : "Escalating Case to Manager..."}</DialogTitle>
              <DialogContent>
                <MSpinner />
              </DialogContent>
            </Dialog>
          </div>
          <div id="completion-container">
            <Dialog
              id="dialog-complete-case"
              open={showCompleteCaseDialog}
              sx={{ "& .MuiDialog-paper": { width: 500 } }}
            >
              <DialogTitle>Mark Case Complete</DialogTitle>
              <DialogContent>
                <TextBlock field="Are you sure?" />
              </DialogContent>
              <DialogActions>{completeCaseActions}</DialogActions>
            </Dialog>
            <Dialog id="dialog-sending-to-manager" open={completingCase} sx={{ "& .MuiDialog-paper": { width: 400 } }}>
              <DialogTitle>Completing Case...</DialogTitle>
              <DialogContent>
                <MSpinner />
              </DialogContent>
            </Dialog>
          </div>
          <div id="switch-case-container">
            <Dialog id="dialog-switch-case" open={showSwitchCaseDialog} sx={{ "& .MuiDialog-paper": { width: 500 } }}>
              <DialogTitle>Switch Case</DialogTitle>
              <DialogContent>
                <TextBlock
                  field={`Are you sure you want to switch to case ${switchCaseDialogControlNum}? Your time will be logged automatically on the old case before switching and your current work will be saved.`}
                />
              </DialogContent>
              <DialogActions>{switchCaseActions}</DialogActions>
            </Dialog>
          </div>
          <div id="rating-container">
            <Dialog
              id="dialog-rating"
              aria-label="Rating Survey"
              open={displayingRatingSurvey}
              sx={{ "& .MuiDialog-paper": { width: 1000 } }}
            >
              <DialogContent>
                <img src={logo} alt="Mueller-Reports-logo" />
                <h6 className="dialog-rating-title">Please rate the quality of this appraisal:</h6>
                <StarRatings
                  name="dialog-rating-score"
                  rating={ratingScore}
                  changeRating={(newRating) => this.setState({ ratingScore: newRating })}
                  starRatedColor="#E03D4C"
                  starHoverColor="#CA2030"
                />
                <TextField
                  id="dialog-rating-comment"
                  variant="standard"
                  type="text"
                  inputProps={{ maxLength: 500 }}
                  label="Comments:"
                  onChange={(e) => this.setState({ ratingComment: e.target.value })}
                  sx={{ minWidth: 350, maxWidth: 350 }}
                />
                <Button onClick={this.submitRating}>Submit</Button>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        {/* Main Content */}
        <div className={"qa-case-views" + (qaPaused ? " paused" : "")}>
          <div>{!qaStarted && qaPaused ? <MSpinner style={{ position: "absolute" }} /> : <div />}</div>
          <div>{qaStarted && (qaPaused || !qaReady) ? <MSpinner style={{ position: "absolute" }} /> : <div />}</div>
          {!qaStarted || qaCase.controlIdNumber === 0 ? (
            <div />
          ) : (
            <div className="qa-case-views">
              {qaCase.qaQueueTypeId === QueueTypeEnum.HomeownersQa ||
              qaCase.qaQueueTypeId === QueueTypeEnum.AuditQa ||
              qaCase.qaQueueTypeId === 0 ? (
                <SummaryView key={`${qaCase.controlIdNumber}-${this.state.summaryKey}`} qaView={this.qaView} />
              ) : (
                <SummaryViewQA2 key={`${qaCase.controlIdNumber}-${this.state.summaryKey}`} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

function getCaseTimeLogWorkType(queueTypeId) {
  const queueTypeMap = {
    [QueueTypeEnum.HomeownersQa]: CaseTimeLogWorkTypeEnum.FIELD_REVIEW,
    [QueueTypeEnum.AppraisalQa]: CaseTimeLogWorkTypeEnum.VALUATION_REVIEW,
    [QueueTypeEnum.FieldAppointment]: CaseTimeLogWorkTypeEnum.FS_SCHEDULING,
    [QueueTypeEnum.PreferredAppointment]: CaseTimeLogWorkTypeEnum.FS_SCHEDULING,
    [QueueTypeEnum.AuditQa]: CaseTimeLogWorkTypeEnum.FS_REVIEW_AUDIT,
  };

  return queueTypeMap[queueTypeId];
}

CaseView.propTypes = {
  getNextCase: PropTypes.func.isRequired,
  getCaseInfo: PropTypes.func.isRequired,
  returnCase: PropTypes.func.isRequired,
  sendToManager: PropTypes.func.isRequired,
  completeCase: PropTypes.func.isRequired,
  getCasePhotos: PropTypes.func.isRequired,
  getCaseSketch: PropTypes.func.isRequired,
  getCaseForms: PropTypes.func.isRequired,
  generatePDF: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  qaCase: PropTypes.object.isRequired,
  caseForms: PropTypes.array,
  requestSuccessful: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  rejectingCase: PropTypes.bool.isRequired,
  escalatingCase: PropTypes.bool.isRequired,
  setQAEscalatingCase: PropTypes.func.isRequired,
  setQARejectingCase: PropTypes.func.isRequired,
  setQAReady: PropTypes.func.isRequired,
  qaReady: PropTypes.bool.isRequired,
  setQAStarted: PropTypes.func.isRequired,
  qaStarted: PropTypes.bool.isRequired,
  setQAPaused: PropTypes.func.isRequired,
  qaPaused: PropTypes.bool.isRequired,
  setQAValidationErrors: PropTypes.func.isRequired,
  validationErrors: PropTypes.array.isRequired,
  setQASummaryViewEnabled: PropTypes.func.isRequired,
  summaryViewEnabled: PropTypes.bool.isRequired,
  setQAShowRejectDialog: PropTypes.func.isRequired,
  showRejectDialog: PropTypes.bool.isRequired,
  setQASwitchCaseDialog: PropTypes.func.isRequired,
  showSwitchCaseDialog: PropTypes.bool.isRequired,
  switchCaseDialogControlNum: PropTypes.number,
  setQAShowSendManagerDialog: PropTypes.func.isRequired,
  showSendToManagerDialog: PropTypes.bool.isRequired,
  setQAShowCompletedCaseDialog: PropTypes.func.isRequired,
  showCompleteCaseDialog: PropTypes.bool.isRequired,
  setQACompletingCase: PropTypes.func.isRequired,
  completingCase: PropTypes.bool.isRequired,
  setQASaveDataFunction: PropTypes.func.isRequired,
  qaSaveDataFunction: PropTypes.func,
  keepGettingCases: PropTypes.bool.isRequired,
  publicPropertyRecord: PropTypes.object.isRequired,
  isFetchingPublicPropertyRecord: PropTypes.bool.isRequired,
  getCaseAlerts: PropTypes.func.isRequired,
  timeSpent: PropTypes.number,
  activeClock: PropTypes.object,
  getActiveClock: PropTypes.func.isRequired,
  addReviewCaseTimeLog: PropTypes.func,
  pnxValidation: PropTypes.object,
  validateForms: PropTypes.func,
  formStatusChange: PropTypes.func,
  makeToast: PropTypes.func.isRequired,
  getPhotos: PropTypes.func,
  rights: PropTypes.array,
  sendToAgent: PropTypes.func,
  setPhoenixRctCommitStatus: PropTypes.func,
  setQATimeSpent: PropTypes.func,
  willLogOut: PropTypes.bool,
  allCaseFormsList: PropTypes.array,
  getAllCaseFormsRedux: PropTypes.func,
  caseAuditFeedbackCompleted: PropTypes.bool,
  quickLists: PropTypes.array,
  resetQACase: PropTypes.func.isRequired,
  qaTimerId: PropTypes.number,
  qaTimerPingIntervalSeconds: PropTypes.number,
  setQATimerInfo: PropTypes.func,
  getQuickLists: PropTypes.func,
  getQuickListCount: PropTypes.func,
  canViewQaDashboard: PropTypes.bool,
};

export default CaseView;
